import { useState, useEffect, useRef } from 'react'
import {
  Search,
  MapPin,
  Star,
  Shield,
  Users,
  ArrowRight,
  ChevronRight,
  Menu,
  X,
  Bike,
  Wrench,
  Tent,
  Baby,
  Waves,
  Mountain,
} from 'lucide-react'

const heroSlides = [
  {
    id: 0,
    image: '/images/marketing/kayaking-lake.jpg',
    label: 'Kayaks & Canoes',
    location: 'Lake Eklutna, AK',
  },
  {
    id: 1,
    image: '/images/marketing/ebike-riding.jpg',
    label: 'E-Bikes',
    location: 'Anchorage, AK',
  },
  {
    id: 2,
    image: '/images/marketing/baby-equipment-hero.jpg',
    label: 'Baby Equipment',
    location: 'Anchorage, AK',
  },
  {
    id: 3,
    image: '/images/marketing/camping-gear-hero.jpg',
    label: 'Camping Gear',
    location: 'Denali, AK',
  },
]

const howItWorksSlides = [
  { id: 0, image: '/images/marketing/howitworks-drone.jpg' },
  { id: 1, image: '/images/marketing/howitworks-soundsystem.jpg' },
  { id: 2, image: '/images/marketing/ebike-riding.jpg' },
]

const categories = [
  {
    name: 'E-Bikes',
    count: 48,
    from: '$35',
    icon: Bike,
    image: '/images/marketing/ebike-riding.jpg',
  },
  {
    name: 'Kayaks',
    count: 31,
    from: '$45',
    icon: Waves,
    image: '/images/marketing/category-kayaks.jpg',
  },
  {
    name: 'Baby Equipment',
    count: 52,
    from: '$15',
    icon: Baby,
    image: '/images/marketing/category-baby-equipment.jpg',
  },
  {
    name: 'Camping Gear',
    count: 112,
    from: '$18',
    icon: Tent,
    image: '/images/marketing/camping-gear-hero.jpg',
  },
  {
    name: 'Power Tools',
    count: 67,
    from: '$25',
    icon: Wrench,
    image: '/images/marketing/category-power-tools.jpg',
  },
  {
    name: 'Ski & Snow',
    count: 38,
    from: '$40',
    icon: Mountain,
    image: '/images/marketing/category-ski-snow.jpg',
  },
]

const listings = [
  {
    id: 1,
    title: 'Trek FX+ Electric Commuter',
    category: 'E-Bikes',
    price: '$38',
    rating: 4.9,
    reviews: 47,
    location: 'Midtown, Anchorage',
    image: '/images/marketing/listing-ebike.jpg',
    badge: 'Top Rated',
  },
  {
    id: 2,
    title: 'Old Town Canoe 16ft',
    category: 'Kayaks',
    price: '$55',
    rating: 4.8,
    reviews: 32,
    location: 'Eagle River',
    image: '/images/marketing/listing-canoe.jpg',
    badge: null,
  },
  {
    id: 3,
    title: 'Makita 18V Power Kit',
    category: 'Tools',
    price: '$29',
    rating: 4.7,
    reviews: 89,
    location: 'South Anchorage',
    image: '/images/marketing/listing-power-tools.jpg',
    badge: null,
  },
  {
    id: 4,
    title: 'UPPAbaby Vista Stroller',
    category: 'Baby Equipment',
    price: '$18',
    rating: 5.0,
    reviews: 24,
    location: 'Downtown, Anchorage',
    image: '/images/marketing/category-baby-equipment.jpg',
    badge: 'Popular',
  },
]

const steps = [
  {
    num: '01',
    title: 'Find what you need',
    body: 'Search by category, location, or date. Browse hundreds of local listings from verified neighbors.',
  },
  {
    num: '02',
    title: 'Book instantly',
    body: 'Send a request or book instantly. Secure payment held until your rental starts.',
  },
  {
    num: '03',
    title: 'Pick up & go',
    body: "Meet your host, grab the gear, and make the most of your day. Return when done.",
  },
]

const navLinks = [
  { label: 'Browse', href: '/browse' },
  { label: 'List an Item', href: '/list-item' },
  { label: 'How It Works', href: '/how-it-works' },
  { label: 'About', href: '/about' },
]

export default function Landing() {
  const [slide, setSlide] = useState(0)
  const [howSlide, setHowSlide] = useState(0)
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [location, setLocation] = useState('')
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setSlide((s) => (s + 1) % heroSlides.length)
    }, 5000)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [])

  useEffect(() => {
    const id = setInterval(() => {
      setHowSlide((s) => (s + 1) % howItWorksSlides.length)
    }, 6000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // This page renders its own full-bleed header (transparent over the hero,
  // solid on scroll) instead of the shared fixed <Navbar />, so it doesn't
  // need App.css's global `body { padding-top: var(--navbar-height) }`
  // (that reserved gap is what pushed the hero image down and left blank
  // body background showing through the transparent header). Cancel it
  // only while this page is mounted.
  useEffect(() => {
    document.body.style.paddingTop = '0'
    return () => {
      document.body.style.paddingTop = ''
    }
  }, [])

  const goToSlide = (i: number) => {
    setSlide(i)
    if (intervalRef.current) clearInterval(intervalRef.current)
    intervalRef.current = setInterval(() => {
      setSlide((s) => (s + 1) % heroSlides.length)
    }, 5000)
  }

  return (
    <div
      className="min-h-screen bg-background text-foreground"
      style={{ fontFamily: "'DM Sans', sans-serif" }}
    >
      {/* ── Navbar ── */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md border-b border-border shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
          <a href="/" className="flex items-center">
            <img
              src={scrolled ? '/IGotThat_logo_bk.svg' : '/IGotThat_logo_wh.svg'}
              alt="I Got That U.S."
              width={230}
              height={34}
              style={{ width: '230px', height: 'auto' }}
            />
          </a>

          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className={`text-sm font-medium transition-colors hover:opacity-70 ${
                  scrolled ? 'text-foreground' : 'text-white/90'
                }`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="/login"
              className={`text-sm font-medium transition-colors ${
                scrolled ? 'text-foreground hover:text-primary' : 'text-white/90 hover:text-white'
              }`}
            >
              Sign in
            </a>
            <a
              href="/register"
              className={`text-sm font-medium transition-colors ${
                scrolled ? 'text-foreground hover:text-primary' : 'text-white/90 hover:text-white'
              }`}
            >
              Sign up
            </a>
            <a
              href="/list-item"
              className="px-4 py-2 rounded-md bg-accent text-accent-foreground text-sm font-semibold hover:bg-amber-400 transition-colors"
            >
              List Your Item
            </a>
          </div>

          <button
            className={`md:hidden ${scrolled ? 'text-foreground' : 'text-white'}`}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-border px-6 py-4 flex flex-col gap-4">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="text-foreground font-medium text-sm py-1"
              >
                {link.label}
              </a>
            ))}
            <a href="/login" className="text-foreground font-medium text-sm py-1">
              Sign in
            </a>
            <a href="/register" className="text-foreground font-medium text-sm py-1">
              Sign up
            </a>
            <a
              href="/list-item"
              className="mt-2 px-4 py-2 rounded-md bg-accent text-accent-foreground text-sm font-semibold text-center"
            >
              List Your Item
            </a>
          </div>
        )}
      </header>

      {/* ── Hero ── */}
      <section className="relative h-screen min-h-[600px] max-h-[900px] overflow-hidden bg-primary">
        {heroSlides.map((s, i) => (
          <div
            key={s.id}
            className="absolute inset-0 transition-opacity duration-1000"
            style={{ opacity: i === slide ? 1 : 0 }}
          >
            <img src={s.image} alt={s.label} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70" />
          </div>
        ))}

        {/* Slide label */}
        <div className="absolute top-24 left-8 md:left-16">
          <span className="px-3 py-1 rounded-full text-xs font-medium bg-white/20 text-white backdrop-blur-sm border border-white/20">
            {heroSlides[slide].label} · {heroSlides[slide].location}
          </span>
        </div>

        {/* Hero content */}
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-6">
          <h1
            className="text-5xl md:text-7xl font-semibold !text-white leading-[1.05] mb-4 max-w-4xl"
            style={{ fontFamily: "'Fraunces', serif", fontOpticalSizing: 'auto' }}
          >
            Rent Anything.
            <br />
            <span className="italic font-light !text-accent">Go Anywhere.</span>
          </h1>
          <p className="text-white/80 text-lg md:text-xl max-w-xl mb-10 font-light">
            E-bikes, kayaks, baby gear, tools, camping equipment — from neighbors near you.
          </p>

          {/* Search bar */}
          <div className="w-full max-w-2xl bg-white rounded-xl shadow-2xl p-2 flex flex-col md:flex-row gap-2">
            <div className="flex items-center gap-2 flex-1 px-3 py-2 rounded-lg bg-secondary">
              <Search size={16} className="text-muted-foreground shrink-0" />
              <input
                type="text"
                placeholder="What do you need? e-bike, kayak..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
              />
            </div>
            <div className="flex items-center gap-2 flex-1 px-3 py-2 rounded-lg bg-secondary md:max-w-[200px]">
              <MapPin size={16} className="text-muted-foreground shrink-0" />
              <input
                type="text"
                placeholder="Location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
              />
            </div>
            <a
              href={`/browse?search=${encodeURIComponent(searchQuery)}`}
              className="px-6 py-3 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors whitespace-nowrap text-center"
            >
              Search
            </a>
          </div>

          {/* Popular tags */}
          <div className="flex flex-wrap justify-center gap-2 mt-4">
            {['E-Bikes', 'Kayaks', 'Baby Gear', 'Power Tools', 'Ski & Snow'].map((tag) => (
              <a
                key={tag}
                href={`/browse?category=${encodeURIComponent(tag)}`}
                className="px-3 py-1 rounded-full text-xs text-white/80 border border-white/30 hover:bg-white/10 transition-colors backdrop-blur-sm"
              >
                {tag}
              </a>
            ))}
          </div>
        </div>

        {/* Slide dots */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {heroSlides.map((_, i) => (
            <button
              key={i}
              onClick={() => goToSlide(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === slide ? 'w-8 bg-accent' : 'w-1.5 bg-white/40'
              }`}
            />
          ))}
        </div>

        {/* Slide thumbnails */}
        <div className="absolute bottom-8 right-6 md:right-10 hidden md:flex gap-2 z-10">
          {heroSlides.map((s, i) => (
            <button
              key={s.id}
              onClick={() => goToSlide(i)}
              className={`w-16 h-10 rounded-md overflow-hidden border-2 transition-all duration-200 ${
                i === slide ? 'border-accent opacity-100' : 'border-transparent opacity-50 hover:opacity-75'
              }`}
            >
              <img src={s.image} alt={s.label} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </section>

      {/* ── Stats strip ── */}
      <section className="bg-primary text-primary-foreground py-8">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { value: '2,400+', label: 'Active listings' },
            { value: '340+', label: 'Verified owners' },
            { value: '98%', label: 'Satisfaction rate' },
            { value: '50+', label: 'Categories' },
          ].map((stat) => (
            <div key={stat.label}>
              <div
                className="text-3xl font-semibold text-accent"
                style={{ fontFamily: "'Fraunces', serif" }}
              >
                {stat.value}
              </div>
              <div className="text-sm text-primary-foreground/70 mt-0.5">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Categories ── */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-xs font-semibold tracking-widest text-primary uppercase mb-2">
                Browse by Category
              </p>
              <h2
                className="text-4xl md:text-5xl font-semibold leading-tight"
                style={{ fontFamily: "'Fraunces', serif" }}
              >
                Rent the right gear
                <br />
                <span className="text-muted-foreground font-light italic">for any adventure</span>
              </h2>
            </div>
            <a
              href="/browse"
              className="hidden md:flex items-center gap-1.5 text-sm font-medium text-primary hover:gap-3 transition-all"
            >
              All categories <ChevronRight size={14} />
            </a>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {categories.map((cat) => {
              const Icon = cat.icon
              return (
                <a
                  key={cat.name}
                  href={`/browse?category=${encodeURIComponent(cat.name)}`}
                  className="group relative overflow-hidden rounded-xl cursor-pointer bg-muted"
                  style={{ aspectRatio: '4/3' }}
                >
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon size={14} className="text-accent" />
                      <span className="text-xs text-white/70 font-medium">{cat.count} available</span>
                    </div>
                    <div className="flex items-end justify-between">
                      <h3
                        className="text-white font-semibold text-base md:text-lg leading-tight"
                        style={{ fontFamily: "'Fraunces', serif" }}
                      >
                        {cat.name}
                      </h3>
                      <span className="text-accent text-sm font-semibold">
                        from {cat.from}
                        <span className="text-white/60 text-xs font-normal">/day</span>
                      </span>
                    </div>
                  </div>
                  <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/0 group-hover:bg-white/10 flex items-center justify-center transition-all duration-200 border border-white/0 group-hover:border-white/30">
                    <ArrowRight size={12} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </a>
              )
            })}
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="relative overflow-hidden py-24 px-6 bg-primary">
        {howItWorksSlides.map((s, i) => (
          <div
            key={s.id}
            className="absolute inset-0 transition-opacity duration-1000"
            style={{ opacity: i === howSlide ? 1 : 0 }}
          >
            <img src={s.image} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/55 to-black/70" />
          </div>
        ))}

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold tracking-widest text-accent uppercase mb-2">
              Simple Process
            </p>
            <h2
              className="text-4xl md:text-5xl font-semibold !text-white"
              style={{ fontFamily: "'Fraunces', serif" }}
            >
              How I Got That U.S. works
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, i) => (
              <div key={step.num} className="relative">
                {i < steps.length - 1 && (
                  <div className="hidden md:block absolute top-6 left-[calc(100%_-_1.5rem)] w-1/2 h-px bg-white/25" />
                )}
                <div className="bg-white/10 backdrop-blur-md rounded-xl p-8 border border-white/20 hover:bg-white/15 transition-colors">
                  <div
                    className="text-5xl font-semibold text-accent mb-4 leading-none"
                    style={{ fontFamily: "'Fraunces', serif" }}
                  >
                    {step.num}
                  </div>
                  <h3
                    className="text-xl font-semibold mb-3 !text-white"
                    style={{ fontFamily: "'Fraunces', serif" }}
                  >
                    {step.title}
                  </h3>
                  <p className="text-white/75 text-sm leading-relaxed">{step.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Featured Listings ── */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-xs font-semibold tracking-widest text-primary uppercase mb-2">Featured</p>
              <h2
                className="text-4xl md:text-5xl font-semibold"
                style={{ fontFamily: "'Fraunces', serif" }}
              >
                Popular near you
              </h2>
            </div>
            <a
              href="/browse"
              className="hidden md:flex items-center gap-1.5 text-sm font-medium text-primary hover:gap-3 transition-all"
            >
              View all listings <ChevronRight size={14} />
            </a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {listings.map((listing) => (
              <a
                key={listing.id}
                href="/browse"
                className="group bg-card rounded-xl overflow-hidden border border-border hover:shadow-lg transition-all duration-200 cursor-pointer"
              >
                <div className="relative overflow-hidden bg-muted" style={{ aspectRatio: '4/3' }}>
                  <img
                    src={listing.image}
                    alt={listing.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {listing.badge && (
                    <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full text-xs font-semibold bg-accent text-accent-foreground">
                      {listing.badge}
                    </span>
                  )}
                  <div className="absolute top-3 right-3 px-2 py-1 rounded-md bg-primary text-primary-foreground text-sm font-semibold">
                    {listing.price}
                    <span className="text-xs font-normal opacity-70">/day</span>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-xs text-primary font-semibold uppercase tracking-wide mb-1">
                    {listing.category}
                  </p>
                  <h4
                    className="font-semibold text-base leading-snug mb-2 line-clamp-1"
                    style={{ fontFamily: "'Fraunces', serif" }}
                  >
                    {listing.title}
                  </h4>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <Star size={12} className="fill-accent text-accent" />
                      <span className="text-sm font-semibold">{listing.rating}</span>
                      <span className="text-xs text-muted-foreground">({listing.reviews})</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <MapPin size={11} />
                      {listing.location}
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── Trust Section ── */}
      <section className="py-20 px-6 bg-primary text-primary-foreground overflow-hidden relative">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-accent blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-white blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto relative">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs font-semibold tracking-widest text-accent uppercase mb-4">
                Why I Got That U.S.
              </p>
              <h2
                className="text-4xl md:text-5xl font-semibold !text-white leading-tight mb-6"
                style={{ fontFamily: "'Fraunces', serif" }}
              >
                Built on trust,
                <br />
                <span className="italic font-light">powered by community</span>
              </h2>
              <p className="text-primary-foreground/70 leading-relaxed mb-8 max-w-md">
                Every listing is from a verified local. We handle payments, insurance, and dispute
                resolution — so you can focus on the adventure.
              </p>
              <a
                href="/browse"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-accent text-accent-foreground font-semibold text-sm hover:bg-amber-400 transition-colors"
              >
                Start browsing <ArrowRight size={15} />
              </a>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                {
                  icon: Shield,
                  title: 'Secure Payments',
                  body: 'Funds held in escrow until your rental is confirmed. Full refunds if anything goes wrong.',
                },
                {
                  icon: Users,
                  title: 'Verified Hosts',
                  body: 'Every owner is ID-verified and reviewed by the community before listing.',
                },
                {
                  icon: Star,
                  title: 'Rated by Renters',
                  body: 'Transparent reviews after every rental. Know exactly what you are getting.',
                },
                {
                  icon: MapPin,
                  title: 'Truly Local',
                  body: 'Real neighbors, real gear, real community. No corporate middleman.',
                },
              ].map((feat) => {
                const Icon = feat.icon
                return (
                  <div key={feat.title} className="bg-white/8 rounded-xl p-5 border border-white/10">
                    <div className="w-9 h-9 rounded-lg bg-accent/20 flex items-center justify-center mb-3">
                      <Icon size={16} className="text-accent" />
                    </div>
                    <h4
                      className="font-semibold text-white mb-1.5 text-sm"
                      style={{ fontFamily: "'Fraunces', serif" }}
                    >
                      {feat.title}
                    </h4>
                    <p className="text-xs text-primary-foreground/60 leading-relaxed">{feat.body}</p>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <section className="py-16 px-6 bg-accent">
        <div className="max-w-3xl mx-auto text-center">
          <h2
            className="text-4xl md:text-5xl font-semibold text-accent-foreground mb-4"
            style={{ fontFamily: "'Fraunces', serif" }}
          >
            Have gear gathering dust?
          </h2>
          <p className="text-accent-foreground/70 mb-8 text-lg font-light">
            Turn idle equipment into income. List your first item free — takes under 5 minutes.
          </p>
          <a
            href="/list-item"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors"
          >
            List your item <ArrowRight size={16} />
          </a>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-foreground text-primary-foreground py-14 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center mb-4">
              <img src="/IGotThat_logo_wh.svg" alt="I Got That U.S." width={271} height={40} style={{ height: '40px', width: 'auto' }} />
            </div>
            <p className="text-sm text-white/50 leading-relaxed max-w-xs">
              Peer-to-peer rentals from verified neighbors. Launching Summer 2026 in Alaska.
            </p>
          </div>
          {[
            {
              head: 'Rent',
              links: ['E-Bikes', 'Kayaks', 'Camping Gear', 'Power Tools', 'Baby Gear'],
            },
            {
              head: 'Company',
              links: ['About Us', 'How It Works', 'List an Item', 'Safety', 'Blog'],
            },
            {
              head: 'Support',
              links: ['Help Center', 'Contact', 'Insurance', 'Terms', 'Privacy'],
            },
          ].map((col) => (
            <div key={col.head}>
              <h5 className="text-xs font-semibold tracking-widest uppercase text-white/40 mb-4">
                {col.head}
              </h5>
              <ul className="flex flex-col gap-2.5">
                {col.links.map((l) => (
                  <li key={l}>
                    <a href="/browse" className="text-sm text-white/60 hover:text-white transition-colors">
                      {l}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="max-w-7xl mx-auto border-t border-white/10 pt-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-xs text-white/30">© 2026 igotthatus.com — All rights reserved</p>
          <p className="text-xs text-white/30">Made with care in Anchorage, Alaska 🏔</p>
        </div>
      </footer>
    </div>
  )
}
